<template>
  <v-menu
    v-if="assignments.length"
    v-model="menuOpen"
    offset-y
    left
    :open-on-hover="canHover"
    :close-on-content-click="false"
    open-delay="60"
    close-delay="160"
    z-index="2400"
    max-width="360">
    <template #activator="{ on, attrs }">
      <span class="wc-task-assignment-info-hitbox" @click.stop="menuOpen = true" @mousedown.stop @pointerdown.stop @touchstart.stop>
        <button
          type="button"
          class="wc-task-assignment-info"
          :class="{ 'wc-task-assignment-info--dark': isDarkMode }"
          aria-label="Zuteilungen anzeigen"
          draggable="false"
          v-bind="attrs"
          v-on="on">
          <v-icon x-small color="success">mdi-account-clock-outline</v-icon>
          <span>{{ compactLabel }}</span>
          <v-icon x-small color="inherit">mdi-information-outline</v-icon>
        </button>
      </span>
    </template>

    <v-card :dark="isDarkMode" class="wc-task-assignment-popover">
      <div class="wc-task-assignment-popover__header">
        <div>
          <div class="wc-task-assignment-popover__eyebrow">ZUTEILUNGEN</div>
          <strong>{{ assignments.length }}× eingeplant</strong>
        </div>
        <v-icon color="success">mdi-calendar-check-outline</v-icon>
      </div>

      <div class="wc-task-assignment-popover__list">
        <div v-for="assignment in assignments" :key="assignment.key" class="wc-task-assignment-popover__item">
          <div class="wc-task-assignment-popover__date">
            <strong>{{ formatDay(assignment.date) }}</strong>
            <span>{{ formatDate(assignment.date) }}</span>
          </div>
          <div class="wc-task-assignment-popover__copy">
            <strong>{{ assignment.time ? assignment.time + " Uhr" : "Zeit nicht hinterlegt" }}</strong>
            <span>
              <v-icon x-small color="inherit">mdi-account-outline</v-icon>
              {{ assignment.employeeName }}
            </span>
          </div>
        </div>
      </div>
    </v-card>
  </v-menu>
</template>

<script>
export default {
  name: "WorkCalendarTaskAssignmentInfo",
  data() {
    return {
      menuOpen: false,
      canHover: false,
    }
  },
  props: {
    templateId: {
      type: [String, Number],
      default: "",
    },
    completions: {
      type: Array,
      default: () => [],
    },
    currentDate: {
      type: String,
      default: "",
    },
    frequency: {
      type: String,
      default: "daily",
    },
    exactTime: {
      type: String,
      default: "",
    },
    isDarkMode: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    assignments() {
      const templateId = String(this.templateId || "")
      if (!templateId || !this.currentDate) return []

      // Never fall back to the system date here. `currentDate` is the date
      // currently opened in WorkCalendar and can briefly be empty while the
      // calendar initializes. Falling back to "today" in that window made a
      // detached Vuetify hover menu show assignments from the real current
      // month even after the user had opened another month.
      const referenceDate = this.$moment(this.currentDate, "YYYY-MM-DD", true)
      if (!referenceDate.isValid()) return []

      const exactTime = this.normalizeTime(this.exactTime)

      return (this.completions || [])
        .filter((entry) => {
          if (!entry || this.resolveTemplateId(entry) !== templateId) return false
          if (entry.completedBy || entry.source === "manual") return false

          const entryDate = this.$moment(entry.date)
          const entryTime = this.normalizeTime(entry.time)
          if (!entryDate.isValid()) return false
          if (this.frequency === "weekly" && !entryDate.isSame(referenceDate, "isoWeek")) return false
          if (this.frequency === "monthly" && !entryDate.isSame(referenceDate, "month")) return false
          if (!["weekly", "monthly"].includes(this.frequency) && !entryDate.isSame(referenceDate, "day")) return false
          if (exactTime && entryTime && entryTime !== exactTime) return false
          return true
        })
        .map((entry, index) => ({
          key: String(entry._id || `${entry.date}-${entry.time || "ohne-zeit"}-${index}`),
          date: entry.date,
          time: this.normalizeTime(entry.time),
          employeeName: entry.assignedEmployeeName || entry.completedByName || "Noch nicht zugewiesen",
        }))
        .sort((a, b) => `${a.date || ""}-${a.time || ""}`.localeCompare(`${b.date || ""}-${b.time || ""}`))
    },
    compactLabel() {
      const first = this.assignments[0]
      if (!first) return ""
      const when = [this.formatCompactDate(first.date), first.time].filter(Boolean).join(" · ")
      const firstLabel = [when, first.employeeName].filter(Boolean).join(" · ")
      return this.assignments.length === 1 ? firstLabel : `${firstLabel} · +${this.assignments.length - 1}`
    },
  },
  mounted() {
    if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
      this.canHover = window.matchMedia("(any-hover: hover) and (any-pointer: fine)").matches
    }
  },
  watch: {
    currentDate() {
      // v-menu renders its content outside the sidebar. Closing it on a date
      // change prevents the old day's/month's detached content from remaining
      // visible while the new daily-view response is loading.
      this.menuOpen = false
    },
  },
  methods: {
    resolveTemplateId(entry) {
      const template = entry && (entry.templateId || entry.template)
      return String((template && template._id) || template || "")
    },
    normalizeTime(value) {
      const match = String(value || "").match(/^(\d{1,2}):(\d{2})/)
      if (!match) return ""
      return `${String(Number(match[1])).padStart(2, "0")}:${match[2]}`
    },
    formatDay(date) {
      const parsed = this.$moment(date)
      return parsed.isValid() ? parsed.format("dd") : "–"
    },
    formatDate(date) {
      const parsed = this.$moment(date)
      return parsed.isValid() ? parsed.format("DD.MM.YYYY") : "Datum fehlt"
    },
    formatCompactDate(date) {
      const parsed = this.$moment(date)
      return parsed.isValid() ? parsed.format("dd DD.MM.") : ""
    },
  },
}
</script>

<style scoped>
.wc-task-assignment-info-hitbox {
  display: block;
  width: 100%;
  min-width: 0;
}

.wc-task-assignment-info {
  width: 100%;
  min-height: 26px;
  margin-top: 7px;
  padding: 4px 7px;
  display: flex;
  align-items: center;
  gap: 5px;
  overflow: hidden;
  color: #157347;
  background: rgba(19, 164, 90, 0.08);
  border: 1px solid rgba(19, 164, 90, 0.18);
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
}

.wc-task-assignment-info--dark {
  color: #8be0ae;
  background: rgba(19, 164, 90, 0.12);
}

.wc-task-assignment-info span {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  font-size: 0.66rem;
  font-weight: 700;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wc-task-assignment-info:focus-visible {
  outline: 3px solid rgba(19, 164, 90, 0.25);
  outline-offset: 2px;
}

.wc-task-assignment-popover {
  width: min(340px, calc(100vw - 24px));
  overflow: hidden;
  border-radius: 15px !important;
}

.wc-task-assignment-popover__header {
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border-bottom: 1px solid rgba(128, 128, 128, 0.16);
}

.wc-task-assignment-popover__eyebrow {
  margin-bottom: 2px;
  color: #13a45a;
  font-size: 0.62rem;
  font-weight: 900;
  letter-spacing: 0.1em;
}

.wc-task-assignment-popover__list {
  max-height: 320px;
  overflow-y: auto;
  padding: 8px;
}

.wc-task-assignment-popover__item {
  display: grid;
  grid-template-columns: 64px minmax(0, 1fr);
  gap: 11px;
  padding: 9px;
  border-radius: 10px;
}

.wc-task-assignment-popover__item + .wc-task-assignment-popover__item {
  border-top: 1px solid rgba(128, 128, 128, 0.12);
}

.wc-task-assignment-popover__date,
.wc-task-assignment-popover__copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.wc-task-assignment-popover__date strong {
  color: #13a45a;
  font-size: 0.72rem;
  text-transform: uppercase;
}

.wc-task-assignment-popover__date span,
.wc-task-assignment-popover__copy span {
  margin-top: 2px;
  color: #7c8794;
  font-size: 0.68rem;
}

.wc-task-assignment-popover__copy strong {
  font-size: 0.78rem;
}

.wc-task-assignment-popover__copy span {
  display: flex;
  align-items: center;
  gap: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (hover: none) and (pointer: coarse) {
  .wc-task-assignment-info {
    min-height: 34px;
  }
}
</style>
